import React, { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  redirect,
  Navigate,
} from 'react-router-dom'
import { UserProvider } from './contexts/userContext'
import { MenuItemsCounterProvider } from './contexts/menuItemsCounterContext'
import Layout from './components/layout/layout'
import PrivateRoute from './features/profile/components/PrivateRoute'
import LoadingFallback from './components/LoadingFallback'
import useCapabilitiesStore from './stores/capabilitiesStore'
import permissions from './features/configuration/utils/rolesPermissions'
import RouterErrorBoundary from './components/RouterErrorBoundary'
import { Toaster } from 'sonner'
import { ThemeProvider } from './contexts/themeContext'
import AuthLayout from './components/layout/AuthLayout/AuthLayout'
import PublicRoute from './guards/PublicRoute'

const LazyDashboard = lazy(() => import('./features/dashboard/pages/Dashboard'))
const LazyBranches = lazy(() => import('./features/dashboard/pages/Branches'))
const LazyBranch = lazy(() => import('./features/dashboard/pages/Branch'))
const LazyDepartment = lazy(() => import('./features/dashboard/pages/Department'))
const LazyOrgChart = lazy(() => import('./features/organizationChart/pages/OrgChart'))
const LazyWorkforceLayout = lazy(() =>
  import('./features/organizationChart/pages/WorkforceLayout'),
)
const LazyWorkforceChartPage = lazy(() =>
  import('./features/organizationChart/pages/WorkforceChartPage'),
)
const LazyWorkforceTableRoute = lazy(() =>
  import('./features/organizationChart/pages/WorkforceTableRoute'),
)
const LazyWorkforceCalculatorPage = lazy(() =>
  import('./features/organizationChart/pages/WorkforceCalculatorPage'),
)
const LazyProfile = lazy(() => import('./features/profile/pages/Profile'))
const LazyConfiguration = lazy(
  () => import('./features/configuration/pages/Configuration'),
)
const LazyDefaults = lazy(
  () => import('./features/defaults/pages/Defaults'),
)
const LazySystemDefaultsScoreConfiguration = lazy(
  () => import('./features/systemDefaults/pages/SystemDefaultsScoreConfiguration'),
)
const LazySystemDefaultsDropdowns = lazy(
  () => import('./features/systemDefaults/pages/SystemDefaultsDropdowns'),
)
const LazySystemDefaultsReferenceFormGeneration = lazy(
  () => import('./features/systemDefaults/pages/SystemDefaultsReferenceFormGeneration'),
)
const LazyTemplates = lazy(() => import('./features/templates/pages/Templates'))
const LazyJobTemplateForm = lazy(
  () => import('./features/templates/pages/JobTemplateFormPage'),
)
const LazyEmailTemplateForm = lazy(
  () => import('./features/templates/pages/EmailTemplateFormPage'),
)
const LazyJobDescriptionTemplateForm = lazy(
  () => import('./features/templates/pages/JobDescriptionTemplateFormPage'),
)
const LazyEvaluationFormTemplateForm = lazy(
  () => import('./features/templates/pages/EvaluationFormTemplateFormPage'),
)
const LazyOfferLetterTemplateForm = lazy(
  () => import('./features/templates/pages/OfferLetterTemplateFormPage'),
)
const LazyReports = lazy(() => import('./features/reports/pages/Reports'))
const LazyChartsReports = lazy(
  () => import('./features/reports/pages/ChartsReports'),
)
const LazyTimeToFillReport = lazy(
  () => import('./features/reports/pages/TimeToFillReport'),
)
const LazyTimeToHireReport = lazy(
  () => import('./features/reports/pages/TimeToHireReport'),
)
const LazyHiringManagerReport = lazy(
  () => import('./features/reports/pages/HiringManagerReport'),
)
const LazyInterviewToHireReport = lazy(
  () => import('./features/reports/pages/InterviewToHireReport'),
)
const LazyRecruiterPerformanceReport = lazy(
  () => import('./features/reports/pages/RecruiterPerformanceReport'),
)
const LazyOfferAcceptanceRateReport = lazy(
  () => import('./features/reports/pages/OfferAcceptanceRateReport'),
)
const LazyVacancyRateReport = lazy(
  () => import('./features/reports/pages/VacancyRateReport'),
)
const LazyRequisitionAgingReport = lazy(
  () => import('./features/reports/pages/RequisitionAgingReport'),
)
const LazyRejectionReasonReport = lazy(
  () => import('./features/reports/pages/RejectionReasonReport'),
)
const LazyCostPerHireReport = lazy(
  () => import('./features/reports/pages/CostPerHireReport'),
)
const LazyReportsLayout = lazy(
  () => import('./features/reports/layout/ReportsLayout'),
)
const LazyPersonalCalendar = lazy(
  () => import('./features/calendar/pages/PersonalCalendar'),
)
const LazyInterviewCalendar = lazy(
  () => import('./features/calendar/pages/InterviewCalendar'),
)
const LazyCandidates = lazy(
  () => import('./features/candidates/pages/candidates'),
)
const LazyBlacklistCandidates = lazy(
  () => import('./features/candidates/pages/blacklistCandidates'),
)
const LazyBlockedCandidates = lazy(
  () => import('./features/candidates/pages/blockedCandidates'),
)
const LazyTalentPools = lazy(
  () => import('./features/talentPools/pages/TalentPools'),
)
const LazyTalentPoolDetails = lazy(
  () => import('./features/talentPools/pages/TalentPoolDetails'),
)
const LazyPositions = lazy(() => import('./features/positions/pages/positions'))
const LazyPositionForm = lazy(
  () => import('./features/positions/pages/PositionForm'),
)
const LazyPosition = lazy(() => import('./features/positions/pages/position'))
const LazyStageInterviews = lazy(
  () => import('./features/positions/pages/stageInterviews'),
)
const LazyInternalApplications = lazy(
  () => import('./features/positions/pages/internalApplications'),
)
const LazyInterviews = lazy(
  () => import('./features/interviews/pages/interviews'),
)
const LazyPrepareInterview = lazy(
  () => import('./features/interviews/pages/prepareInterview'),
)
const LazyRequisitionManagement = lazy(
  () => import('./features/requisitions/pages/RequisitionManagement'),
)
const LazyRequisitionDetails = lazy(
  () =>
    import(
      './features/requisitions/components/requisitions/RequisitionDetails'
    ),
)
// const LazyRequisitionForm = lazy(() => import('./Dashboard/pages/RequisitionForm'));
const LazyRequisitionCreationContainer = lazy(
  () => import('./features/requisitions/pages/RequisitionCreationContainer'),
)
const LazyJobRequests = lazy(
  () => import('./features/approvalRequests/pages/jobRequests'),
)
const LazyOrganizations = lazy(
  () => import('./features/organizations/pages/organizations'),
)
const LazyOrganization = lazy(
  () => import('./features/organizations/pages/Organization'),
)
const LazyOrganizationForm = lazy(
  () => import('./features/organizations/pages/OrganizationForm'),
)
const LazyOrganizationInfo = lazy(
  () => import('./features/organizations/pages/SingleOrganizationDetails'),
)
const LazyEvaluationForms = lazy(
  () => import('./features/evaluationForms/pages/EvaluationForms'),
)
const LazyDataManagement = lazy(
  () => import('./features/dataManagement/pages/dataManagement'),
)
const LazyNotifications = lazy(
  () => import('./features/notifications/pages/Notifications'),
)
const LazyNotFound = lazy(() => import('./features/profile/pages/NotFound'))
const LazyApprovalRequest = lazy(
  () => import('./features/approvalRequests/pages/approvalRequest'),
)
const LazyUploadRequestedDocuments = lazy(
  () => import('./features/interviews/pages/UploadRequestedDocuments'),
)
const LazySubscriptionSuccessPage = lazy(
  () => import('./features/subscriptions/pages/SubscriptionSuccessPage'),
)
const LazyPlanSelectionPage = lazy(
  () =>
    import(
      './features/subscriptions/components/userConfiguration/PlanSelection'
    ),
)
const LazyGreenhouseProfilePage = lazy(
  () => import('./features/greenhouseProfile/pages/InterviewAnalysis'),
)
const LazyAssistinoPage = lazy(
  () => import('../src/features/Assistino/pages/Assistino'),
)

const LazyDisplaySettings = lazy(
  () => import('./features/settings/pages/DisplaySettings'),
)

const LazyReferenceRequestForm = lazy(
  () => import('./features/referenceCheck/pages/ReferenceRequestForm'),
)

const LazyReferenceFeedbackForm = lazy(
  () => import('./features/referenceCheck/pages/ReferenceFeedbackForm'),
)
const LazyConversations = lazy(
  () => import('./features/Conversations/pages/Conversations'),
)

const LazyPublicVideoLink = lazy(
  () => import('./features/interviews/pages/PublicVideoLink'),
)

const LazyOfferDetails = lazy(
  () => import('./features/offers/pages/OfferDetails'),
)
const LazyOfferRequisitionDetails = lazy(
  () => import('./features/offers/pages/OfferRequisitionDetails'),
)
const LazyOffers = lazy(() => import('./features/offers/pages/Offers'))
const LazyBudgetConfiguration = lazy(
  () => import('./features/budgetConfiguration/pages/BudgetConfiguration'),
)

const LazyAssessments = lazy(
  () => import('./features/assessments/pages/Assessments'),
)
const LazyEmployees = lazy(() => import('./features/employees/pages/Employees'))
const LazyTrainingHub = lazy(() => import('./features/trainingHub/pages/TrainingHub'))
const LazyMyLearning = lazy(() => import('./features/myLearning/pages/MyLearning'))
const LazyTrainingPlayer = lazy(() => import('./features/myLearning/pages/TrainingPlayer'))

// ===== Auth
const Login = lazy(() => import('./features/auth/pages/Login'))
const Registration = lazy(() => import('./features/auth/pages/Registration'))
const ForgetPassword = lazy(
  () => import('./features/auth/pages/ForgetPassword'),
)
const ResetPassword = lazy(() => import('./features/auth/pages/ResetPassword'))
const UserRegistration = lazy(
  () => import('./features/auth/pages/UserRegistration'),
)
const ExpiredLink = lazy(() => import('./features/auth/pages/ExpiredLink'))
const MainLayout = () => (
  <MenuItemsCounterProvider>
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  </MenuItemsCounterProvider>
)

const createPermissionLoader = (permission) => async () => {
  const store = useCapabilitiesStore.getState()
  if (!store.loaded && !store.loading) {
    await store.ensureLoaded()
  }
  if (!useCapabilitiesStore.getState().hasPermission(permission)) throw redirect('/not-found')
  return null
}

const createFeatureLoader = (feature) => async () => {
  const store = useCapabilitiesStore.getState()
  if (!store.loaded && !store.loading) {
    await store.ensureLoaded()
  }
  if (!useCapabilitiesStore.getState().isEnabled(feature)) throw redirect('/not-found')
  return null
}

const routerConfig = [
  {
    path: '/',
    element: <PrivateRoute />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            element: <Outlet />,
            children: [
              {
                path: '/',
                element: <LazyDashboard />,
                // loader: createPermissionLoader(permissions.companyDashboard)
              },
              {
                path: '/branches',
                element: <LazyBranches />,
                loader: createFeatureLoader('org_units_branch_department'),
              },
              {
                path: '/branches/:id',
                element: <LazyBranch />,
                loader: createFeatureLoader('org_units_branch_department'),
              },
              {
                path: '/departments/:id',
                element: <LazyDepartment />,
                loader: createFeatureLoader('org_units_branch_department'),
              },
              {
                path: '/profile',
                element: <LazyProfile />,
                // loader: createPermissionLoader(permissions.companyUpdate),
              },
              {
                path: '/organization-chart',
                element: <LazyOrgChart />,
                // loader: createPermissionLoader(permissions.companyUpdate),
              },
              {
                path: '/assistino',
                element: <LazyAssistinoPage />,
              },
              {
                path: '/assistino/:id',
                element: <LazyAssistinoPage />,
              },
              {
                path: '/workforce-structure',
                element: <LazyWorkforceLayout />,
                loader: createPermissionLoader(permissions.reportsSourcing),
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate to="/workforce-structure/chart" replace />
                    ),
                  },
                  {
                    path: 'chart',
                    element: <LazyWorkforceChartPage />,
                  },
                  {
                    path: 'table',
                    element: <LazyWorkforceTableRoute />,
                  },
                  {
                    path: 'calculator',
                    element: <LazyWorkforceCalculatorPage />,
                  },
                ],
              },
              {
                path: '/configuration',
                element: <LazyConfiguration />,
                loader: createPermissionLoader(permissions.roleReadAll),
              },
              {
                path: '/defaults',
                element: <LazyDefaults />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/system-defaults',
                loader: () => redirect('/system-defaults/score-configuration'),
              },
              {
                path: '/system-defaults/score-configuration',
                element: <LazySystemDefaultsScoreConfiguration />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/system-defaults/dropdowns',
                element: <LazySystemDefaultsDropdowns />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/system-defaults/reference-form-generation',
                element: <LazySystemDefaultsReferenceFormGeneration />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/system-defaults/templates',
                element: <LazyTemplates />,
                loader: createPermissionLoader(permissions.jobTemplatesRead),
              },
              {
                path: '/system-defaults/templates/form/:positionId',
                element: <LazyJobTemplateForm />,
                loader: createPermissionLoader(permissions.jobTemplatesManage),
              },
              {
                path: '/system-defaults/templates/:templateId/preview',
                element: <LazyJobTemplateForm />,
                loader: createPermissionLoader(permissions.jobTemplatesRead),
              },
              {
                path: '/system-defaults/templates/:templateId/edit',
                element: <LazyJobTemplateForm />,
                loader: createPermissionLoader(permissions.jobTemplatesManage),
              },
              {
                path: '/system-defaults/templates/email/new',
                element: <LazyEmailTemplateForm />,
                loader: createPermissionLoader(permissions.emailTemplatesManage),
              },
              {
                path: '/system-defaults/templates/email/:templateId/edit',
                element: <LazyEmailTemplateForm />,
                loader: createPermissionLoader(permissions.emailTemplatesManage),
              },
              {
                path: '/system-defaults/job-description-templates/new',
                element: <LazyJobDescriptionTemplateForm />,
                loader: createPermissionLoader(permissions.jobDescriptionTemplatesManage),
              },
              {
                path: '/system-defaults/job-description-templates/:templateId/edit',
                element: <LazyJobDescriptionTemplateForm />,
                loader: createPermissionLoader(permissions.jobDescriptionTemplatesManage),
              },
              {
                path: '/system-defaults/job-description-templates/:templateId/preview',
                element: <LazyJobDescriptionTemplateForm />,
                loader: createPermissionLoader(permissions.jobDescriptionTemplatesRead),
              },
              {
                path: '/system-defaults/evaluation-form-templates/new',
                element: <LazyEvaluationFormTemplateForm />,
                loader: createPermissionLoader(permissions.evaluationFormTemplatesManage),
              },
              {
                path: '/system-defaults/evaluation-form-templates/:templateId/edit',
                element: <LazyEvaluationFormTemplateForm />,
                loader: createPermissionLoader(permissions.evaluationFormTemplatesManage),
              },
              {
                path: '/system-defaults/evaluation-form-templates/:templateId/preview',
                element: <LazyEvaluationFormTemplateForm />,
                loader: createPermissionLoader(permissions.evaluationFormTemplatesRead),
              },
              {
                path: '/system-defaults/offer-letter-templates/new',
                element: <LazyOfferLetterTemplateForm />,
                loader: createPermissionLoader(permissions.offerLetterTemplatesManage),
              },
              {
                path: '/system-defaults/offer-letter-templates/:templateId/edit',
                element: <LazyOfferLetterTemplateForm />,
                loader: createPermissionLoader(permissions.offerLetterTemplatesManage),
              },
              {
                path: '/system-defaults/offer-letter-templates/:templateId/preview',
                element: <LazyOfferLetterTemplateForm />,
                loader: createPermissionLoader(permissions.offerLetterTemplatesRead),
              },
              {
                path: '/local-candidates',
                element: <LazyCandidates />,
                loader: createPermissionLoader(permissions.candidateReadAll),
              },
              {
                path: '/blacklist-candidates',
                element: <LazyBlacklistCandidates />,
                loader: createPermissionLoader(
                  permissions.candidateReadBlacklisted,
                ),
              },
              {
                path: '/blocked-candidates',
                element: <LazyBlockedCandidates />,
                loader: createPermissionLoader(
                  permissions.candidateReadBlacklisted,
                ),
              },
              {
                path: '/talent-pools',
                element: <LazyTalentPools />,
                loader: createPermissionLoader(permissions.candidateRead),
              },
              {
                path: '/talent-pools/:id',
                element: <LazyTalentPoolDetails />,
                loader: createPermissionLoader(permissions.candidateRead),
              },
              {
                path: '/positions',
                element: <LazyPositions />,
                loader: createPermissionLoader(permissions.positionRead),
              },
              { path: '/positions/new', element: <LazyPositionForm /> },
              { path: '/positions/:id', element: <LazyPosition /> },
              {
                path: '/positions/:positionId/stages/:stageId',
                element: <LazyStageInterviews />,
              },
              {
                path: '/positions/:positionId/internal-applications',
                element: <LazyInternalApplications />,
              },
              {
                path: '/requisitions',
                element: <LazyRequisitionManagement />,
                loader: createPermissionLoader(
                  permissions.requisitionsReadHistory,
                ),
              },
              {
                path: '/requisitions/new',
                element: <LazyRequisitionCreationContainer />,
                loader: createPermissionLoader(permissions.requisitionsCreate),
              },
              {
                path: '/requisitions/:id',
                element: <LazyRequisitionDetails />,
              },
              {
                path: '/job-requests',
                element: <LazyJobRequests />,
                loader: createPermissionLoader(permissions.jobRequestsRead),
              },
              {
                path: '/organizations',
                element: <LazyOrganizations />,
                loader: createPermissionLoader(permissions.organizationReadAll),
              },
              { path: '/organizations/new', element: <LazyOrganizationForm /> },
              { path: '/organizations/:id', element: <LazyOrganization /> },
              {
                path: '/organizations/:id/info',
                element: <LazyOrganizationInfo />,
              },
              { path: '/interviews/:id', element: <LazyInterviews /> },
              {
                path: '/interviews/:token/questions',
                element: <LazyPrepareInterview />,
              },
              { path: '/success', element: <LazySubscriptionSuccessPage /> },
              { path: '/plan-selection', element: <LazyPlanSelectionPage /> },
              {
                path: '/data-management',
                element: <LazyDataManagement />,
                loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/conversations',
                element: <LazyConversations />,
                // loader: createPermissionLoader(permissions.reportsSourcing),
              },
              {
                path: '/offers',
                element: <LazyOffers />,
                loader: createPermissionLoader(
                  permissions.offerRead,
                ),
              },
              {
                path: '/employees',
                element: <LazyEmployees />,
                loader: createPermissionLoader(
                  permissions.onboardingEmployeeRead,
                ),
              },
              {
                path: '/training-hub',
                element: <LazyTrainingHub />,
                loader: createPermissionLoader(
                  permissions.trainingCreate,
                ),
              },
              {
                path: '/my-learning',
                element: <LazyMyLearning />,
                loader: createPermissionLoader(
                  permissions.trainingAssignmentMyLearnings,
                ),
              },
              {
                path: '/my-learning/:id',
                element: <LazyTrainingPlayer />,
                loader: createPermissionLoader(
                  permissions.trainingAssignmentMyLearnings,
                ),
              },
              {
                path: '/offers/:id',
                element: <LazyOfferDetails />,
                loader: createPermissionLoader(
                  permissions.offerRead,
                ),
              },
              {
                path: '/offer-requisitions/:id',
                element: <LazyOfferRequisitionDetails />,
                loader: createPermissionLoader(
                  permissions.offerRead,
                ),
              },
              {
                path: '/budget-configuration',
                element: <LazyBudgetConfiguration />,
                loader: createPermissionLoader(permissions.userReadAll),
              },
              {
                path: '/reports',
                element: <LazyReportsLayout />,
                children: [
                  {
                    path: 'sourcing',
                    element: <LazyReports />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'charts/:category',
                    element: <LazyChartsReports />,
                  },
                  {
                    path: 'time-to-fill',
                    element: <LazyTimeToFillReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'time-to-hire',
                    element: <LazyTimeToHireReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'interview-to-hire',
                    element: <LazyInterviewToHireReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'hiring-manager-performance',
                    element: <LazyHiringManagerReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'recruiter-performance',
                    element: <LazyRecruiterPerformanceReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'offer-acceptance-rate',
                    element: <LazyOfferAcceptanceRateReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'vacancy-rate',
                    element: <LazyVacancyRateReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'requisition-aging',
                    element: <LazyRequisitionAgingReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'rejection-reasons',
                    element: <LazyRejectionReasonReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                  {
                    path: 'cost-per-hire',
                    element: <LazyCostPerHireReport />,
                    loader: createPermissionLoader(permissions.reportsSourcing),
                  },
                ],
              },
            ],
          },
          {
            children: [
              {
                path: '/personal-calendar',
                element: <LazyPersonalCalendar />,
                loader: createPermissionLoader(
                  permissions.userPersonalCalendar,
                ),
              },
              {
                path: '/interview-schedule',
                element: <LazyInterviewCalendar />,
                loader: createPermissionLoader(
                  permissions.reportsInterviewSchedule,
                ),
              },
              { path: '/notifications', element: <LazyNotifications /> },
              {
                path: '/evaluation-forms',
                element: <LazyEvaluationForms />,
                loader: createPermissionLoader(
                  permissions.evaluationFormsReadAll,
                ),
              },
              {
                path: '/assessments',
                element: <LazyAssessments />,
              },
              {
                path: '/display-settings',
                element: <LazyDisplaySettings />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: '/login',
        element: <Login />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/register',
        element: <Registration />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/forget-password',
        element: <ForgetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/reset-password/:token',
        element: <ResetPassword />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/user-registration/:token',
        element: <UserRegistration />,
        errorElement: <RouterErrorBoundary />,
      },
      {
        path: '/expired-link',
        element: <ExpiredLink />,
        errorElement: <RouterErrorBoundary />,
      },
    ],
  },
  {
    path: '/greenhouse/profile/:token',
    element: <LazyGreenhouseProfilePage />,
  },
  {
    path: '/approval-request/:approvalToken',
    element: <LazyApprovalRequest />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/request-documents/:token',
    element: <LazyUploadRequestedDocuments />,
    errorElement: <RouterErrorBoundary />,
  },
  {
    path: '/reference-request-form/:requestToken',
    element: <LazyReferenceRequestForm />,
  },
  {
    path: '/reference-feedback-form/:contactToken',
    element: <LazyReferenceFeedbackForm />,
  },
  {
    path: '/interview/:interview_id/question/:question_id/video',
    element: <LazyPublicVideoLink />,
  },
  {
    path: '*',
    element: <LazyNotFound />,
    errorElement: <RouterErrorBoundary />,
  },
]

const router = createBrowserRouter(routerConfig, {
  basename: '/',
})

function App() {
  return (
    <UserProvider>
      <ThemeProvider>
        <RouterProvider router={router} />
        <Toaster richColors />
      </ThemeProvider>
    </UserProvider>
  )
}

export default App
